import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatKg } from '@/lib/format';

import { useBuyerProposals } from './useOutbounds';
import type {
  BuyerProposalOption,
  ConfirmOutboundSalesResultInput,
  OutboundDto,
  OutboundSalesResultLineInput,
} from './types';

interface SalesResultLineForm extends OutboundSalesResultLineInput {
  item_name_snapshot: string;
  material_id: string | null;
  kg: string;
}

const linesFromOutbound = (outbound: OutboundDto | null): SalesResultLineForm[] =>
  (outbound?.lines ?? []).map((line) => ({
    outbound_line_id: line.outbound_line_id,
    item_name_snapshot: line.item_name_snapshot,
    material_id: line.material_id,
    kg: line.kg,
    unit_price: line.unit_price ?? '',
    price_reference_id: line.price_reference_id ?? '',
    buyer_proposal_id: line.buyer_proposal_id ?? '',
  }));

interface SalesResultDialogProps {
  outbound: OutboundDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (body: ConfirmOutboundSalesResultInput) => Promise<void>;
  pending: boolean;
  errorMessage?: string | null;
  destinationCompanyId?: string;
}

export const SalesResultDialog = ({
  outbound,
  open,
  onOpenChange,
  onConfirm,
  pending,
  errorMessage,
  destinationCompanyId,
}: SalesResultDialogProps) => {
  const { t } = useTranslation('sales');
  const [lines, setLines] = useState<SalesResultLineForm[]>([]);
  const requiresBuyerProposal = outbound?.destination_type_snapshot === 'buyer';
  const proposalsQuery = useBuyerProposals(
    outbound?.monthly_batch_id,
    open && requiresBuyerProposal,
  );

  useEffect(() => {
    if (open) setLines(linesFromOutbound(outbound));
  }, [open, outbound]);

  const proposalOptionsByLine = useMemo(() => {
    const byLine = new Map<string, BuyerProposalOption[]>();
    for (const line of lines) {
      byLine.set(
        line.outbound_line_id,
        (proposalsQuery.data ?? []).filter(
          (proposal) =>
            proposal.status === 'approved' &&
            proposal.vendor_company_id === destinationCompanyId &&
            (proposal.material_id === null || proposal.material_id === line.material_id),
        ),
      );
    }
    return byLine;
  }, [destinationCompanyId, lines, proposalsQuery.data]);

  const canSubmit = useMemo(
    () =>
      lines.length > 0 &&
      lines.every((line) => {
        if (line.unit_price.trim() === '') return false;
        const unitPrice = Number(line.unit_price);
        if (!Number.isFinite(unitPrice) || unitPrice < 0) return false;
        if (!requiresBuyerProposal) return true;
        return (proposalOptionsByLine.get(line.outbound_line_id) ?? []).some(
          (proposal) => proposal.buyer_proposal_id === line.buyer_proposal_id,
        );
      }),
    [lines, proposalOptionsByLine, requiresBuyerProposal],
  );

  const updateLine = (outboundLineId: string, patch: Partial<SalesResultLineForm>) => {
    setLines((current) =>
      current.map((line) =>
        line.outbound_line_id === outboundLineId ? { ...line, ...patch } : line,
      ),
    );
  };

  const submit = async () => {
    await onConfirm({
      lines: lines.map((line) => ({
        outbound_line_id: line.outbound_line_id,
        unit_price: line.unit_price.trim(),
        price_reference_id: line.price_reference_id?.trim() || null,
        buyer_proposal_id: requiresBuyerProposal ? line.buyer_proposal_id?.trim() || null : null,
      })),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('efs.sales.outbounds.sales_result.title', {
              defaultValue: '販売実績を確定',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.sales.outbounds.sales_result.subtitle', {
              defaultValue:
                '出荷後に確定した単価を明細ごとに入力します。販売金額は重量 × 単価で計算されます。',
            })}
          </DialogDescription>
        </DialogHeader>

        {errorMessage || proposalsQuery.error ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{errorMessage ?? proposalsQuery.error?.message}</AlertDescription>
          </Alert>
        ) : null}

        <div className="space-y-3">
          {lines.map((line) => {
            const fieldPrefix = `sales-result-${line.outbound_line_id}`;
            return (
              <div
                key={line.outbound_line_id}
                className="grid gap-3 rounded-md border border-efs-border p-3 sm:grid-cols-12"
              >
                <div className="sm:col-span-3">
                  <div className="text-sm font-medium">{line.item_name_snapshot}</div>
                  <div className="text-xs text-efs-text-muted">{formatKg(line.kg)} kg</div>
                </div>

                <div className="space-y-1 sm:col-span-3">
                  <label className="text-xs font-medium" htmlFor={`${fieldPrefix}-unit-price`}>
                    {t('efs.sales.outbounds.fields.unit_price', { defaultValue: '単価' })}
                  </label>
                  <Input
                    id={`${fieldPrefix}-unit-price`}
                    inputMode="decimal"
                    value={line.unit_price}
                    onChange={(event) =>
                      updateLine(line.outbound_line_id, { unit_price: event.target.value })
                    }
                    placeholder="0"
                  />
                </div>

                <div className="space-y-1 sm:col-span-3">
                  <label className="text-xs font-medium" htmlFor={`${fieldPrefix}-price-reference`}>
                    {t('efs.sales.outbounds.fields.price_reference_id', {
                      defaultValue: '価格参照 ID（任意）',
                    })}
                  </label>
                  <Input
                    id={`${fieldPrefix}-price-reference`}
                    value={line.price_reference_id ?? ''}
                    onChange={(event) =>
                      updateLine(line.outbound_line_id, {
                        price_reference_id: event.target.value,
                      })
                    }
                  />
                </div>

                {requiresBuyerProposal ? (
                  <div className="space-y-1 sm:col-span-3">
                    <label
                      className="text-xs font-medium"
                      htmlFor={`${fieldPrefix}-buyer-proposal`}
                    >
                      {t('efs.sales.outbounds.fields.buyer_proposal_id', {
                        defaultValue: '承認済み買方提案',
                      })}
                    </label>
                    <Select
                      value={line.buyer_proposal_id ?? ''}
                      onValueChange={(value) => {
                        const proposal = (
                          proposalOptionsByLine.get(line.outbound_line_id) ?? []
                        ).find((option) => option.buyer_proposal_id === value);
                        updateLine(line.outbound_line_id, {
                          buyer_proposal_id: value,
                          unit_price:
                            line.unit_price || proposal?.estimated_unit_price || line.unit_price,
                        });
                      }}
                    >
                      <SelectTrigger id={`${fieldPrefix}-buyer-proposal`}>
                        <SelectValue
                          placeholder={t(
                            'efs.sales.outbounds.sales_result.buyer_proposal_placeholder',
                            { defaultValue: '承認済み提案を選択' },
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {(proposalOptionsByLine.get(line.outbound_line_id) ?? []).map(
                          (proposal) => (
                            <SelectItem
                              key={proposal.buyer_proposal_id}
                              value={proposal.buyer_proposal_id}
                            >
                              {proposal.reason}
                              {proposal.estimated_unit_price
                                ? ` · ${proposal.estimated_unit_price}`
                                : ''}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    {!proposalsQuery.isLoading &&
                    (proposalOptionsByLine.get(line.outbound_line_id) ?? []).length === 0 ? (
                      <p className="text-xs text-destructive">
                        {t('efs.sales.outbounds.sales_result.no_approved_buyer_proposal', {
                          defaultValue: 'この出荷先・素材に使える承認済み提案がありません。',
                        })}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button type="button" disabled={!canSubmit || pending} onClick={submit}>
            {pending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('efs.sales.outbounds.actions.sales_result_confirm', {
              defaultValue: '販売実績確定',
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
